
import { defineComponent, onBeforeMount, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// types
import { SectorT } from '@/core/types/sectors.types'
import { DispatcherCourierT } from '@/core/types/couriers.types'

// constants
import { SubmitOrdersTypes } from '@/core/constants/dispatcher.constants'

// enums
import { DispatcherSubmitOrdersTypesEnum } from '@/core/enums/dispatcher.enums'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'

// realisations
import { dispatcher } from '@/core/realisations/dispatcher.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-select': TheSelect,
    },
    props: {
        dispatcherSectorId: {
            type: Number,
            required: true,
        },
    },
    emits: ['cancel', 'submited'],
    setup(props, { emit }) {
        const state = reactive({
            type: null as string | null,
            sector: null as number | null,
            courier: null as number | null,
            is_loading: false,
        })

        const stateValidation = {
            type: { required },
            sector: {
                required: requiredIf(() => {
                    if (state.type === DispatcherSubmitOrdersTypesEnum.onsector) return true
                    return false
                }),
            },
            courier: {
                required: requiredIf(() => {
                    if (state.type === DispatcherSubmitOrdersTypesEnum.onsector) return true
                    return false
                }),
            },
        }

        const state_form$ = useVuelidate(stateValidation, state)

        const emitCancel = () => {
            emit('cancel')
        }

        const sectors_state = reactive(new DataState<SectorT>({}))

        const loadSectors = async () => {
            try {
                sectors_state.is_loading = true

                const response = await dispatchers_sectors.getSectors(props.dispatcherSectorId)
                sectors_state.list = response.data

                sectors_state.is_failed = false
                sectors_state.failure.error_state.is_cancel = false
            } catch (error) {
                sectors_state.failure.catchError(error)

                if (!sectors_state.failure.error_state.is_cancel) {
                    sectors_state.is_failed = true
                }
            } finally {
                if (!sectors_state.failure.error_state.is_cancel) {
                    sectors_state.is_loading = false
                }
            }
        }

        const couriers_state = reactive(new DataState<DispatcherCourierT>({}))

        const loadCouriers = async (page: number, search?: string) => {
            couriers_state.pagination.current_page = page

            try {
                couriers_state.is_loading = true

                const response = await couriers.getList({
                    page,
                    name: search,
                    dispatcherSectorIds: [props.dispatcherSectorId],
                    statusIds: [2],
                })

                couriers_state.list = response.data
                couriers_state.pagination.total_pages = response.meta.last_page

                couriers_state.is_failed = false
                couriers_state.failure.error_state.is_cancel = false
            } catch (error) {
                couriers_state.failure.catchError(error)

                if (!couriers_state.failure.error_state.is_cancel) {
                    couriers_state.is_failed = true
                }
            } finally {
                if (!couriers_state.failure.error_state.is_cancel) {
                    couriers_state.is_loading = false
                }
            }
        }

        const loadMoreCouriers = async (page: number) => {
            if (page > couriers_state.pagination.total_pages) return
            couriers_state.pagination.current_page = page

            try {
                couriers_state.is_loading = true

                const response = await couriers.getList({
                    page,
                    dispatcherSectorIds: [props.dispatcherSectorId],
                    statusIds: [2],
                })

                couriers_state.list = couriers_state.list.concat(response.data)

                couriers_state.pagination.total_pages = response.meta.last_page

                couriers_state.is_failed = false
                couriers_state.failure.error_state.is_cancel = false
            } catch (error) {
                couriers_state.failure.catchError(error)

                if (!couriers_state.failure.error_state.is_cancel) {
                    couriers_state.is_failed = true
                }
            } finally {
                if (!couriers_state.failure.error_state.is_cancel) {
                    couriers_state.is_loading = false
                }
            }
        }

        const searchCourier = async (search_query: string) => {
            // data_state.countries.pagination.current_page
            loadCouriers(1, search_query)
        }

        const unshiftFindedCourier = async (courier: DispatcherCourierT) => {
            await loadCouriers(couriers_state.pagination.current_page)
            couriers_state.list.unshift(courier)
        }

        onBeforeMount(() => {
            loadSectors()
            loadCouriers(1)
        })

        const submitOrdersToDispatcherSector = async () => {
            const answer = confirm(
                'Вы действительно хотите выдать все заказы на диспетчерский сектор?'
            )
            if (!answer) return

            try {
                state.is_loading = true

                await dispatcher.submitOrdersToDispatcherSector({
                    dispatchers_sector_id: props.dispatcherSectorId,
                })

                emit('submited')
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const submitOrdersToSector = async () => {
            if (!state.sector || !state.courier) return

            const answer = confirm('Вы действительно хотите выдать заказы на сектор?')
            if (!answer) return

            try {
                state.is_loading = true

                await dispatcher.submitOrdersToSector({
                    courier_id: state.courier,
                    sector_id: state.sector,
                })

                emit('submited')
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const submitOrders = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (state.type === DispatcherSubmitOrdersTypesEnum.ondispatchersector) {
                await submitOrdersToDispatcherSector()
                return
            }

            if (state.type === DispatcherSubmitOrdersTypesEnum.onsector) {
                await submitOrdersToSector()
                return
            }
        }

        return {
            state,
            state_form$,
            emitCancel,
            submitOrders,
            SubmitOrdersTypes,
            DispatcherSubmitOrdersTypesEnum,
            sectors_state,
            loadSectors,
            couriers_state,
            loadCouriers,
            loadMoreCouriers,
            searchCourier,
            unshiftFindedCourier,
        }
    },
})
